/**
 * UI-version flag for the side-copy redesign.
 *
 * Drives the `data-design` attribute on <html>. App.tsx — and ONLY App.tsx —
 * branches on `getUI()` to dispatch to a v2 page or the legacy page. No
 * other file imports this module.
 *
 * Lifecycle:
 *   - `?ui=v2` / `?ui=legacy` in the URL persists to localStorage and is
 *     stripped from the address bar on next paint.
 *   - Default is `'v2'` (the redesign). Visitors who explicitly chose legacy
 *     (stored, or via `?ui=legacy`) keep it; everyone else gets v2.
 */
import { useSyncExternalStore } from 'react';

export type UIVersion = 'legacy' | 'v2';

const STORAGE_KEY = 'voxcore.ui';
const QUERY_PARAM = 'ui';
const VALID: ReadonlyArray<UIVersion> = ['legacy', 'v2'];

/** Default UI when nothing is stored. The redesign is now the default;
 *  `?ui=legacy` (and a stored 'legacy' preference) still opt out. */
const DEFAULT_UI: UIVersion = 'v2';

function readStored(): UIVersion {
  if (typeof window === 'undefined') return DEFAULT_UI;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'v2' || v === 'legacy' ? v : DEFAULT_UI;
}

/** Consume `?ui=` on startup. Returns true if the stored value changed. */
export function consumeUIQueryParam(): boolean {
  if (typeof window === 'undefined') return false;
  const url = new URL(window.location.href);
  const raw = url.searchParams.get(QUERY_PARAM);
  if (!raw) return false;
  const next: UIVersion = raw === 'v2' ? 'v2' : 'legacy';
  const prior = readStored();
  if (next !== prior) window.localStorage.setItem(STORAGE_KEY, next);
  url.searchParams.delete(QUERY_PARAM);
  window.history.replaceState({}, '', url.toString());
  return next !== prior;
}

/** Set `<html data-design="v2">` (or unset) before React renders. */
export function applyDesignAttribute() {
  if (typeof document === 'undefined') return;
  const ui = readStored();
  if (ui === 'v2') document.documentElement.setAttribute('data-design', 'v2');
  else document.documentElement.removeAttribute('data-design');
}

/** Persist + reload so the route dispatcher picks the correct lazy chunk. */
export function setUI(next: UIVersion, reload: boolean = true) {
  if (typeof window === 'undefined') return;
  if (!VALID.includes(next)) return;
  window.localStorage.setItem(STORAGE_KEY, next);
  applyDesignAttribute();
  if (reload) window.location.reload();
}

const listeners = new Set<() => void>();

function subscribe(cb: () => void) {
  listeners.add(cb);
  const onStorage = (e: StorageEvent) => { if (e.key === STORAGE_KEY) cb(); };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener('storage', onStorage);
  };
}

function getSnapshot(): UIVersion { return readStored(); }
function getServerSnapshot(): UIVersion { return DEFAULT_UI; }

/** Reactive — used inside v2 components if they need to subscribe. */
export function useUI(): UIVersion {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Non-reactive — used in App.tsx route dispatch. */
export function getUI(): UIVersion {
  return readStored();
}
